// node_modules
import React, { FC, useContext, useEffect, useState } from "react";
// Types
import { TSynonymDTO } from "Types";
// Components
import { FindestTextBox } from "Components";
import { SynonymsButton } from "../Synonyms/SynonymsButton";
// Enums
import { SearchPriorityEnum, SearchSubTermTypeEnum } from "Enums";
import { BorderedIconedTextblock } from "Components/Shared";
// Styles
import variables from "styles/colors.module.scss";
import { UserContext } from "Providers";
import { FeatureToggleConstants } from "Constants";

type TSearchSubTermProps = {
    isSynonymsSectionShown: boolean,
    searchSubTermSearchPriority: SearchPriorityEnum,
    searchSubTerm: string,
    searchSubTermSynonyms: TSynonymDTO[],
    searchSubTermType: SearchSubTermTypeEnum,
    isDisabled?: boolean
    termGroupName?: string,
    handleEditSearchSubTerm: (newSearchSubTermValue: string, searchSubTermType: SearchSubTermTypeEnum) => void,
    handleOpenSynonymsSection: (event: React.MouseEvent) => void,
    handleCloseSynonymsSection: () => void,
    onTextFocus?: (event: React.FocusEvent) => void
}

export const SearchSubTerm: FC<TSearchSubTermProps> = ({ isSynonymsSectionShown,
        searchSubTermSearchPriority, searchSubTerm, searchSubTermSynonyms,
        searchSubTermType, isDisabled, termGroupName, handleEditSearchSubTerm, handleOpenSynonymsSection,
        handleCloseSynonymsSection }: TSearchSubTermProps) => {

    // Context
    const { isCurrentUserFindest } = useContext(UserContext);

    // const
    const isSearchTermGroupsEnabled = FeatureToggleConstants.SynonymGroups && isCurrentUserFindest;

    // State
    const [subTerm, setSubTerm] = useState<string>(searchSubTerm);

    useEffect(() => {
        setSubTerm(searchSubTerm);
    }, [searchSubTerm]);

    useEffect(() => {
        // when current sub term is edited,
        // after 500 ms do:
        const delayDebounceFn = setTimeout(() => {
            // remove white spaces at the start and end of the new sub term
            const newSubTerm = subTerm.trim();
            // if new sub term is null/empty/undefined, set new sub term to the old one
            if (!newSubTerm) {
                setSubTerm(searchSubTerm);
                return;
            }
            // if the new sub term is the same as the old one, do nothing
            if (newSubTerm.trim().toLowerCase() === searchSubTerm.trim().toLowerCase()) return;
            // set sub term state
            setSubTerm(newSubTerm);
            // call parent on sub term change
            handleEditSearchSubTerm(newSubTerm, searchSubTermType);
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [subTerm, searchSubTerm, handleEditSearchSubTerm, searchSubTermType]);

    const openSynonymsSection = (event: React.MouseEvent): void => {
        // if the search sub term is enabled, show synonyms section
        if (searchSubTermSearchPriority !== SearchPriorityEnum.Disabled) handleOpenSynonymsSection(event);
    };

    const closeSynonymsSection = (): void => {
        // hide synonyms section
        handleCloseSynonymsSection();
    };

    return (
        <div>
            {isSearchTermGroupsEnabled && termGroupName ? <BorderedIconedTextblock text="group" backgroundColor={variables.findestPurple} color="white" /> : null }
            <FindestTextBox value={isSearchTermGroupsEnabled && termGroupName ? termGroupName : subTerm} isReadonly={isSearchTermGroupsEnabled && termGroupName ? true : false} />
            <div style={{display: isDisabled ? "none" : "flex"}}>
                <SynonymsButton
                    isSynonymsSectionShown={isSynonymsSectionShown}
                    synonymsCount={isSearchTermGroupsEnabled && termGroupName ? searchSubTermSynonyms.length + 1 : searchSubTermSynonyms.length}
                    handleOpenSynonymsSection={openSynonymsSection}
                    handleCloseSynonymsSection={closeSynonymsSection} />
            </div>            
        </div>
    );
};
